import { IColumnSettings } from '../../../entities/IClusters';

/** People-picker keys that store the same principal under Id vs ChildId (etc.). */
const IDENTITY_FILTER_PROPERTIES = new Set(['id', 'childid', 'principalid', 'userid']);

const filterKeyParts = (key: string): { base: string; property: string | undefined } => {
    const dot = key.indexOf('.');
    if (dot < 0) {
        return { base: key, property: undefined };
    }
    return { base: key.slice(0, dot), property: key.slice(dot + 1) };
};

const isIdentityFilterProperty = (property: string | undefined): boolean => (
    !!property && IDENTITY_FILTER_PROPERTIES.has(property.toLowerCase())
);

/**
 * True when two FilterBar keys are the same control (exact match) or identity aliases
 * on the same field (`actors.Id` ↔ `actors.ChildId`). Distinct properties on one field
 * (`lastStatusUpdate.status` vs `lastStatusUpdate.occuredAt`) are not aliases (#69082).
 */
export const areFilterKeysAliases = (a: string, b: string): boolean => {
    if (!a || !b) {
        return false;
    }
    if (a === b) {
        return true;
    }
    const left = filterKeyParts(a);
    const right = filterKeyParts(b);
    if (left.base !== right.base || !left.property || !right.property) {
        return false;
    }
    return isIdentityFilterProperty(left.property) && isIdentityFilterProperty(right.property);
};

export const getFilterKeysForColumn = (
    field: string,
    allKeys: string[],
    resolveTopicFromField?: (field: string) => string | void,
    filterKeyToColumnField?: Record<string, string>
): string[] => allKeys.filter((key) => {
    if (key === field || key.startsWith(`${field}.`)) {
        return true;
    }
    if (filterKeyToColumnField?.[key] === field) {
        return true;
    }
    const resolvedKey = resolveTopicFromField?.(key);
    if (typeof resolvedKey === 'string' && (resolvedKey === field || resolvedKey.startsWith(`${field}.`))) {
        return true;
    }
    if (resolveTopicFromField) {
        const resolvedField = resolveTopicFromField(field);
        if (typeof resolvedField === 'string' && typeof resolvedKey === 'string' && resolvedField === resolvedKey) {
            return true;
        }
    }
    return false;
});

/**
 * Drops hidden-filters entries that refer to filters of columns which are not visible (#66023).
 */
export function pruneHiddenFiltersForHiddenColumns(
    hiddenFilters: string[] | undefined,
    columnsSettings: IColumnSettings[],
    availableFilterKeys: string[] | undefined,
    resolveTopicFromField?: (field: string) => string | void,
    filterKeyToColumnField?: Record<string, string>
): string[] | undefined {
    if (!hiddenFilters?.length) {
        return hiddenFilters;
    }
    const hiddenColFields = new Set(
        columnsSettings.filter(c => c.isVisible === false && typeof c.field === 'string').map(c => c.field)
    );
    if (!hiddenColFields.size) {
        return hiddenFilters;
    }

    const keysPool = availableFilterKeys?.length ? availableFilterKeys : hiddenFilters;

    const pruned = hiddenFilters.filter(key => {
        for (const hf of hiddenColFields) {
            const keysForCol = getFilterKeysForColumn(hf, keysPool, resolveTopicFromField, filterKeyToColumnField);
            if (keysForCol.includes(key)) {
                return false;
            }
        }
        return true;
    });
    return pruned.length ? pruned : undefined;
}

/** True if any hidden filter key applies to a currently visible column (for table-settings “active” state). */
export function hasHiddenFiltersAffectingVisibleColumns(
    hiddenFilters: Iterable<string> | undefined,
    columnsSettings: IColumnSettings[],
    availableFilterKeys: string[] | undefined,
    resolveTopicFromField?: (field: string) => string | void,
    filterKeyToColumnField?: Record<string, string>
): boolean {
    if (!hiddenFilters || !columnsSettings.length) {
        return false;
    }
    const keys = [...hiddenFilters];
    if (!keys.length) {
        return false;
    }
    const hiddenFilterSet = new Set(keys);
    const visibleCols = columnsSettings.filter(c => c.isVisible !== false);
    const keyPool = availableFilterKeys?.length ? availableFilterKeys : keys;

    for (const col of visibleCols) {
        if (typeof col.field !== 'string') {
            continue;
        }
        const forCol = getFilterKeysForColumn(col.field, keyPool, resolveTopicFromField, filterKeyToColumnField);
        if (forCol.some(k => hiddenFilterSet.has(k))) {
            return true;
        }
    }
    return false;
}
